import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

import BpkSectionHeader from '@skyscanner-internal/backpack-web/bpk-component-section-header';

import CardRail from '../CardRail';
import SelectedChips from '../SelectedChips';
import ThemeCard from '../ThemeCard';
import ThemeCardV2 from '../ThemeCardV2';

import type { CardRailWithThemeCardProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/CardRailWithThemeCardProps';
import type { Chip } from '@skyscanner-internal/landing-pages-shared-types/types-shared/ThemeCardProps';
import type { ThemeCardV2Props } from '@skyscanner-internal/landing-pages-shared-types/types-shared/ThemeCardV2Props';

import STYLES from './CardRailWithThemeCard.module.scss';

type CardRailWithThemeCardPropsWithExtraProps = CardRailWithThemeCardProps & {
  callToAction?: ReactNode;
};

const CardRailWithThemeCard = ({
  callToAction = null,
  cards,
  cardsV2 = [],
  disclaimer,
  grouping,
  imageConfig,
  onCardClick,
  subtitle,
  title,
  useThemeCardV2 = false,
}: CardRailWithThemeCardPropsWithExtraProps) => {
  const [activeChipIndex, setActiveChipIndex] = useState(0);
  const activeCards = useThemeCardV2 ? cardsV2 : cards;
  const [renderCards, setRenderCards] =
    useState<typeof activeCards>(activeCards);
  const chips = grouping
    ? Array.from(
        new Map(
          activeCards
            .map((item) => item.groupKey)
            .filter((key): key is Chip => {
              if (key !== undefined) {
                return !!key?.name;
              }

              return false;
            })
            .map((key) => {
              const id = key?.name;
              return [id, key]; // 👈 id as the unique key
            }),
        ).values(),
      )
    : [];
  useEffect(() => {
    if (grouping) {
      if (activeChipIndex < chips.length) {
        setRenderCards(
          (activeCards as ThemeCardV2Props[]).filter((card) => {
            if (card.groupKey !== undefined) {
              return card.groupKey?.name === chips[activeChipIndex]?.name;
            }
            return false;
          }),
        );
      }
    }
  }, [activeChipIndex]);

  if (!activeCards.length) {
    return null;
  }

  return (
    <div className={STYLES.CardRailWithThemeCard}>
      <div className={STYLES.CardRailWithThemeCard__header}>
        <BpkSectionHeader title={title} description={subtitle} />
      </div>
      <SelectedChips
        chips={chips}
        activeChipIndex={activeChipIndex}
        setActiveChipIndex={setActiveChipIndex}
        cardType="Rail"
      />
      <CardRail disclaimer={disclaimer} callToAction={callToAction}>
        {useThemeCardV2
          ? (renderCards as ThemeCardV2Props[]).map((card, index) => (
              <div
                key={card.title}
                data-testid={`themeCardGroup${index}`}
                className={String(STYLES.CardRail__card)}
              >
                <ThemeCardV2 {...card} index={index} />
              </div>
            ))
          : (renderCards as typeof cards).map((card, index) => (
              <div
                key={card.title}
                data-testid={`themeCardGroup${index}`}
                className={String(STYLES.CardRail__card)}
              >
                <ThemeCard
                  {...card}
                  index={index}
                  imageConfig={imageConfig}
                  onClick={onCardClick && (() => onCardClick(card))}
                />
              </div>
            ))}
      </CardRail>
    </div>
  );
};

export default CardRailWithThemeCard;
